import { useRef, useCallback, useEffect } from "react";
import { useIceRestartMetrics } from "./useIceRestartMetrics";
import { useIceServerFailover } from "./useIceServerFailover";
import { useIceRestartBackoff } from "./useIceRestartBackoff";

export const useIceRestart = ({
  pcRef,
  socketRef,
  targetRef,
  onStateChange,
}) => {
  const metrics = useIceRestartMetrics();
  const backoff = useIceRestartBackoff();
  const failover = useIceServerFailover();

  // 🔒 Safety guards
  const restartingRef = useRef(false);
  const hasEverConnectedRef = useRef(false);

  /**
   * SAFE ICE RESTART
   */
  const restartIce = useCallback(async () => {
    const pc = pcRef.current;
    const socket = socketRef.current;
    const target = targetRef.current;

    if (!pc || !socket || !target) return;

    // 🚫 Never restart before first successful connection
    if (!hasEverConnectedRef.current) {
      console.warn("[ICE] Skip restart: never connected yet");
      return;
    }

    // 🚫 Avoid parallel restarts
    if (restartingRef.current) {
      console.warn("[ICE] Restart already in progress");
      return;
    }

    // 🚫 Signaling must be stable
    if (pc.signalingState !== "stable") {
      console.warn("[ICE] Skip restart: signaling not stable");
      return;
    }

    // 🚫 Backoff exhausted
    if (backoff.exhausted()) {
      console.error("[ICE] Restart attempts exhausted");
      onStateChange?.("failed");
      return;
    }

    restartingRef.current = true;
    metrics.markAttempt();
    onStateChange?.("restarting");

    try {
      // 🔁 Escalate ICE config if needed (TURN priority, relay-only, etc.)
      const config = failover.getCurrentIceConfig();
      pc.setConfiguration(config);

      const offer = await pc.createOffer({ iceRestart: true });
      await pc.setLocalDescription(offer);

      socket.emit("webrtc-offer", {
        to: target,
        offer,
        iceRestart: true,
      });

      // Success path handled via connectionstatechange
    } catch (err) {
      console.error("[ICE] Restart failed", err);

      metrics.markFailure();
      failover.escalateTier();

      restartingRef.current = false;

      setTimeout(() => {
        restartIce();
      }, backoff.getDelay());
    }
  }, [pcRef, socketRef, targetRef]);

  /**
   * CONNECTION STATE HANDLER
   * Call this from pc.onconnectionstatechange
   */
  const handleIceStateChange = useCallback(
    (state) => {
      const pc = pcRef.current;
      if (!pc) return;

      if (state === "connected") {
        hasEverConnectedRef.current = true;
        restartingRef.current = false;

        metrics.markSuccess();
        backoff.reset();
        failover.resetTier();

        onStateChange?.("connected");
      }

      if (state === "failed") {
        metrics.markFailure();
        failover.escalateTier();

        restartingRef.current = false;
        onStateChange?.("failed");

        // 🔥 Auto-restart only if previously connected
        if (hasEverConnectedRef.current) {
          setTimeout(() => {
            restartIce();
          }, backoff.getDelay());
        }
      }

      if (state === "disconnected") {
        onStateChange?.("reconnecting");
      }
    },
    [restartIce]
  );

  /**
   * AUTO-WIRE PC EVENTS (IMPORTANT)
   */
  useEffect(() => {
    const pc = pcRef.current;
    if (!pc) return;

    pc.onconnectionstatechange = () => {
      handleIceStateChange(pc.connectionState);
    };

    pc.oniceconnectionstatechange = () => {
      console.log("[ICE] iceConnectionState:", pc.iceConnectionState);
    };
  }, [handleIceStateChange]);

  return {
    restartIce,
    handleIceStateChange,
    metrics,
  };
};
